import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { ErrorMessage, WarningMessage } from '@bonusly/common-admin';
import {
	Box,
	Button,
	Divider,
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Stack,
	Text,
	Textarea,
	useToast,
} from '@bonusly/design-system';
import { useIdentity } from '@bonusly/who-am-i';

import { RichTextDisplay } from '../../rich-text-editor';
import { QueryKey, useCreateApprovalRequest, useGetIncentiveDetails } from '../hooks/use-incentives';

export interface ClaimIncentiveModalProps {
	incentiveId: string;
	isOpen: boolean;
	onClose: VoidFunction;
}

interface FormValues {
	claim_reason: string;
}

const ClaimIncentiveModal: FC<ClaimIncentiveModalProps> = ({ incentiveId, isOpen, onClose }) => {
	const { t } = useTranslation('announcements');
	const toast = useToast();
	const { track } = useAnalytics();
	const queryClient = useQueryClient();
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const { refetchProfile } = useIdentity();
	const { data: incentive } = useGetIncentiveDetails(incentiveId);
	const { mutate: createApprovalRequest, isLoading: isClaiming } = useCreateApprovalRequest();

	// form setup
	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
	} = useForm<FormValues>();

	// clear error message, reset claim reason and track view on open
	useEffect(() => {
		if (!isOpen) return;
		track(AnalyticsEvent.Announcements.ClaimIncentiveModalViewed);
		setErrorMessage(null);
		setValue('claim_reason', '');
	}, [isOpen, setValue, track]);

	const handleClose = () => {
		track(AnalyticsEvent.Announcements.ClaimIncentiveModalClosed);
		onClose();
	};

	const onSubmit = (formValues: FormValues) => {
		if (errorMessage) setErrorMessage(null);
		track(AnalyticsEvent.Announcements.ClaimIncentiveRequestSubmitted);

		createApprovalRequest(
			{
				award_id: incentiveId,
				claim_reason: formValues.claim_reason,
				giver_id: incentive?.giver_id || '',
			},
			{
				onError: () => {
					track(AnalyticsEvent.Announcements.ClaimIncentiveFailure);
					setErrorMessage(t('modal.claim_error_message'));
				},
				onSuccess: () => {
					queryClient.invalidateQueries([QueryKey.IncentiveData, incentiveId]);
					// NOTE: invalidating query and refetching profile to keep both user data calls up to date
					// bonus-feed MFE uses 'recognition-user'
					queryClient.invalidateQueries([QueryKey.UserProfile]);
					refetchProfile();
					const message = incentive?.auto_approved ? t('modal.claim_success_message_auto_approved') : t('modal.claim_success_message_approval_needed');
					toast({
						title: message,
						status: 'success',
						duration: null, // Persist until manually closed
						isClosable: true,
					});
					track(AnalyticsEvent.Announcements.ClaimIncentiveSuccess);
					handleClose();
				},
			}
		);
	};

	const isDeleted = incentive?.deleted_at !== null;
	const isInactive = incentive?.state === 'inactive' && !isDeleted;
	const isOutOfStock = incentive?.out_of_stock;

	const isClaimDisabled = isClaiming || !incentive || isOutOfStock || isInactive || isDeleted;

	return (
		<Modal autoFocus={false} isOpen={isOpen} onClose={handleClose} scrollBehavior="inside" size="2xl">
			<ModalOverlay />
			<ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
				<ModalHeader borderBottomWidth={rem(1)} borderColor="border.soft" borderStyle="solid" color="text.default">
					{t('modal.claim_header')}
				</ModalHeader>
				<ModalCloseButton data-testid="close-button" size="lg" borderRadius="full" />

				<ModalBody px={5} pt={5} pb={0}>
					{incentive ? (
						<Stack spacing={5}>
							{incentive.stock_message && !isDeleted && !isInactive && (
								<Box bgColor={incentive.out_of_stock ? 'error.bg' : 'warning.bg'} borderRadius="sm" data-testid="claim-limit-message" px={2} py={1}>
									{incentive.out_of_stock ? <ErrorMessage>{incentive.stock_message}</ErrorMessage> : <WarningMessage>{incentive.stock_message}</WarningMessage>}
								</Box>
							)}
							{isDeleted && (
								<Box bgColor="error.bg" borderRadius="sm" data-testid="deleted-message" px={2} py={1}>
									<ErrorMessage>{t('modal.incentive_deleted_message')}</ErrorMessage>
								</Box>
							)}
							{isInactive && (
								<Box bgColor="error.bg" borderRadius="sm" data-testid="inactive-message" px={2} py={1}>
									<ErrorMessage>{t('modal.incentive_inactive_message')}</ErrorMessage>
								</Box>
							)}
							<Flex>
								<Box data-testid="incentive-image" maxW="200px">
									<Box borderRadius="lg" margin="0 auto">
										<Box
											as="span"
											dangerouslySetInnerHTML={{
												__html: incentive.award_avatar,
											}}
										/>
									</Box>
								</Box>
								<Box ml={4}>
									<Text data-testid="incentive-name" fontWeight="semibold" fontSize={100} lineHeight={6}>
										{incentive.name}
									</Text>
									<RichTextDisplay>{incentive.description}</RichTextDisplay>
								</Box>
							</Flex>
							<FormControl isInvalid={!!errors.claim_reason}>
								<StyledFormLabel fontSize={50} lineHeight={5}>
									{t('modal.claim_reason_field')}
								</StyledFormLabel>
								<Textarea
									data-testid="reason-textarea"
									borderColor="border.input"
									_focusVisible={{
										borderColor: errors.claim_reason ? 'error.inputBorder' : 'border.input',
									}}
									_hover={{
										borderColor: errors.claim_reason ? 'error.inputBorder' : 'border.input',
									}}
									cursor="text"
									resize="none"
									{...register('claim_reason', {
										required: t('modal.claim_reason_required'),
									})}
								/>
								<FormErrorMessage>{errors.claim_reason?.message}</FormErrorMessage>
							</FormControl>
						</Stack>
					) : (
						<Text px={1} fontSize={100} color="text.softer">
							{t('modal.no_incentive_data')}
						</Text>
					)}
				</ModalBody>

				<Divider mt={5} />

				<ModalFooter flexDirection="column">
					<Flex justify="space-between" w="100%">
						<Button variant="alpha" colorScheme="secondary" data-testid="cancel-button" onClick={handleClose}>
							{t('modal.cancel_button')}
						</Button>
						<Button colorScheme="primary" data-testid="claim-button" isDisabled={isClaimDisabled} type="submit">
							{t('modal.claim_button')}
						</Button>
					</Flex>
					{errorMessage && (
						<Text color="error.text" fontSize={50} mt={4} textAlign="right" w="100%">
							{errorMessage}
						</Text>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export { ClaimIncentiveModal };

const StyledFormLabel = ({ ...props }) => {
	return <FormLabel _disabled={{ color: 'text.default' }} {...props} />;
};
